"use client";

import {
	Badge,
	Button,
	Heading,
	Image,
	Tag,
	Text
} from "@pamperedchef/design-system";
import type { Cta, CmsImage } from "@/composites/landing/types";
import type { KitProductView } from "../KitDetailsModal";
import { KitPrice } from "../KitPrice";
import * as styles from "./KitCard.module.scss";

/**
 * CMS payload shape for one kit card. Mirrors the `kit_options.kits[]` entry —
 * the `KitOptionsGrid` reads this off the block and feeds the flat `KitCardProps`
 * to each `KitCard`. Kept separate from `KitCardProps` so the composite stays
 * agnostic of the CMS envelope while the dispatcher does the shape translation.
 */
export interface KitCardData {
	badge_text?: string;
	sale_badge_text?: string;
	kit_product: KitProductView;
	price_text?: string;
	original_price_text?: string;
	percent_discount_text?: string;
	marketing_description?: string;
	image?: CmsImage;
	select_cta?: Cta;
	view_details_label?: string;
}

export interface KitCardProps {
	title: string;
	badgeText?: string;
	saleBadgeText?: string;
	priceText?: string;
	originalPriceText?: string;
	percentDiscountText?: string;
	marketingDescription?: string;
	image?: { url: string; alt?: string };
	selectCta?: Cta;
	viewDetailsLabel?: string;
	/** Called when the "view details" affordance is activated. */
	onViewDetails?: () => void;
}

/**
 * One kit option in the kit-options grid — a light mint-tint card with an
 * image, an optional dark "PC'S PICK" badge, a teal serif title, bold price,
 * marketing copy, an outlined "Select" CTA, and a tertiary "See what's
 * included" link that fires `onViewDetails`. Client island — it attaches the
 * details handler that the parent grid uses to open the modal.
 */
export function KitCard({
	title,
	badgeText,
	saleBadgeText,
	priceText,
	originalPriceText,
	percentDiscountText,
	marketingDescription,
	image,
	selectCta,
	viewDetailsLabel = "See what's included",
	onViewDetails
}: KitCardProps) {
	return (
		<article className={styles["kit-card"]} data-component="KitCard">
			{image ? (
				<div className={styles["kit-card__media"]}>
					<Image
						src={image.url}
						alt={image.alt ?? title}
						className={styles["kit-card__image"]}
						objectFit="cover"
					/>
					{badgeText ? (
						<span className={styles["kit-card__badge"]}>
							<Badge
								label={badgeText}
								kind="inverted"
								size="sm"
								icon={{ name: "guarantee" }}
							/>
						</span>
					) : null}
				</div>
			) : null}

			<div className={styles["kit-card__body"]}>
				<div className={styles["kit-card__info"]}>
					{saleBadgeText ? (
						<span className={styles["kit-card__sale-badge"]}>
							<Tag
								label={saleBadgeText}
								kind="subtle"
								color="red"
								size="sm"
							/>
						</span>
					) : null}

					<Heading
						tag="h3"
						styledAs="h4"
						className={styles["kit-card__title"]}
					>
						{title}
					</Heading>

					{priceText ? (
						<KitPrice
							priceText={priceText}
							originalPriceText={originalPriceText}
							percentDiscountText={percentDiscountText}
						/>
					) : null}

					{marketingDescription ? (
						<Text
							tag="p"
							styledAs="mdr"
							className={styles["kit-card__description"]}
						>
							{marketingDescription}
						</Text>
					) : null}
				</div>

				<div className={styles["kit-card__actions"]}>
					{selectCta ? (
						<Button
							kind="secondary"
							size="lg"
							onPress={() => {
								window.location.href = selectCta.href;
							}}
						>
							{selectCta.title}
						</Button>
					) : null}

					{onViewDetails ? (
						<Button
							kind="tertiary"
							size="sm"
							onPress={onViewDetails}
						>
							{viewDetailsLabel}
						</Button>
					) : null}
				</div>
			</div>
		</article>
	);
}
